"use client";

import React, { useEffect, useCallback, useState } from "react";
import Joyride, { STATUS, EVENTS } from "react-joyride";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/navigation";

import services from "@/store/services";
import api from "@/api";
import "./index.scss";

function BatchTour({ stepsData, tourFor }) {
  const userDetails = useSelector((state) => state?.userDetails);
  const theme = useSelector((state) => state?.theme);
  const [siteTour, setSiteTour] = useState({
    run: false,
    steps: [],
    stepIndex: 0,
  });
  const dispatch = useDispatch();
  const router = useRouter();

  const updateShowTour = async () => {
    setSiteTour({ run: false, steps: [], stepIndex: 0 });
    await api.updateShowTour({ id: userDetails._id });
    dispatch(services.fetchUserDetails());
  };

  // Initialize Tour Steps
  const initializeTourSteps = useCallback(() => {
    if (stepsData?.length) {
      setTimeout(() => {
        setSiteTour((prev) => ({
          ...prev,
          run: true,
          steps: stepsData.map((step, index) => ({
            ...step,
            disableBeacon: true, // Disable the beacon for all steps
            key: `step-${index}`,
          })),
        }));
      }, 200);
    }
  }, [stepsData]);

  // Handle Joyride Callback
  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, index, step, type, status } = data;
      const { next, previous } = step?.data || {};
      const isPreviousAction = action === "prev";

      if (type === EVENTS.STEP_AFTER) {
        if (next || previous) {
          setSiteTour((prev) => ({
            ...prev,
            run: true,
            stepIndex: index + (isPreviousAction ? -1 : 1),
          }));

          router.push(isPreviousAction && previous ? previous : next);
        } else if (index === siteTour.steps.length - 1) {
          setSiteTour({ run: false, steps: [], stepIndex: 0 });
        }
      }

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        // End the tour if skipped or finished
        setSiteTour({ run: false, steps: [], stepIndex: 0 });

        updateShowTour();
        dispatch(services.fetchUserDetails());
      }
    },
    [siteTour.steps]
  );
  // Resume the tour after navigation completes

  useEffect(() => {
    const step = stepsData[siteTour.stepIndex];
    if (step) {
      setTimeout(() => {
        const target = document.querySelector(step.target);
        if (target) {
          setSiteTour((prev) => ({ ...prev, run: true }));
        }
      }, 200);
    }
  }, [stepsData, siteTour.stepIndex]);

  // Initialize the tour when the component mounts
  useEffect(() => {
    if (userDetails && stepsData?.length) {
      initializeTourSteps();
    }
  }, [userDetails, stepsData, initializeTourSteps]);

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      run={siteTour.run}
      stepIndex={siteTour.stepIndex}
      hideCloseButton={true}
      showSkipButton={true}
      // debug={true}
      steps={siteTour.steps}
      locale={{
        last: "Complete",
        skip: <p aria-label="skip">skip</p>,
      }}
      disableScrolling={false}
      disableScrollParentFix={false}
      styles={{
        options: {
          zIndex: 1000,
          textColor: theme ? "#fff" : "#000",
          // textColor: "var(--text-color)",
          primaryColor: "#f3912e",
          backgroundColor: "var(--background)",
          padding: "0px",
        },
      }}
    />
  );
}

export default BatchTour;
